import { useEffect, useRef, useState } from "react";
import { EventType } from "@ag-ui/core";
import { createAnalyticsAssistant } from "../../../services/AnalyticsAssistant";
import { getAssistantTool } from "../../../services/AnalyticsAssistantTools";

function createSession() {
  return { threadId: crypto.randomUUID(), agent: null, active: null, lastId: null };
}

export function useAssistantChat({ startDate, endDate }) {
  const session = useRef(null);
  if (!session.current) session.current = createSession();
  const [messages, setMessages] = useState([]);
  const validPeriod = Boolean(startDate && endDate && startDate <= endDate);

  useEffect(() => {
    return () => {
      const current = session.current;
      session.current = null;
      current?.active?.controller.abort();
      current?.agent?.abortRun();
      void current?.agent?.detachActiveRun();
    };
  }, []);

  useEffect(() => {
    const current = session.current;
    if (!current?.active) return;
    setMessages((previous) =>
      previous.map((message) => (message.status === "pending" ? { ...message, status: "stopped" } : message)),
    );
    current.active.controller.abort();
    current.agent?.abortRun();
    void current.agent?.detachActiveRun();
  }, [startDate, endDate]);

  async function send(question, request, retryId) {
    if (!validPeriod || !question.trim() || session.current?.active) return;
    if (!session.current) {
      session.current = createSession();
    }
    const current = session.current;
    current.period = { startDate, endDate };
    if (retryId && retryId !== current.lastId) return;
    const id = retryId || crypto.randomUUID();
    const controller = new AbortController();
    current.active = { controller };
    current.lastId = id;
    const update = (change) => {
      if (session.current !== current || controller.signal.aborted) return;
      setMessages((previous) =>
        previous.map((message) => (message.id === id ? { ...message, ...change } : message)),
      );
    };
    setMessages((previous) => {
      const item = { id, question: question.trim(), request, status: "pending", text: "" };
      return retryId ? previous.map((message) => (message.id === id ? item : message)) : [...previous, item];
    });
    if (request) {
      try {
        const data = await getAssistantTool(request, { startDate, endDate, signal: controller.signal });
        if (session.current !== current || controller.signal.aborted) return;
        update({ status: "success", data });
      } catch {
        update({ status: "error" });
      } finally {
        current.active = null;
      }
      return;
    }
    if (!current.agent) {
      current.agent = createAnalyticsAssistant(() => current.period, current.threadId);
    }
    const agent = current.agent;
    const runId = crypto.randomUUID();
    const beforeMessages = structuredClone(agent.messages);
    const beforeState = structuredClone(agent.state);
    const previousIds = new Set(beforeMessages.map((message) => message.id));
    let answer = "";
    let finished = false;
    let failed = false;
    let interrupted = false;
    agent.addMessage({ id, role: "user", content: question.trim() });
    // Bound a stalled stream as well as the initial HTTP request.
    const timeout = setTimeout(() => {
      update({ status: "error" });
      controller.abort();
      agent.abortRun();
      void agent.detachActiveRun();
    }, 120000);
    try {
      await agent.runAgent(
        { runId, abortController: controller },
        {
          onMessagesChanged: ({ messages: agentMessages }) => {
            answer = agentMessages
              .filter(
                (message) =>
                  !previousIds.has(message.id) &&
                  message.role === "assistant" &&
                  typeof message.content === "string",
              )
              .map((message) => message.content)
              .join("\n\n");
            update({ text: answer, ...(answer ? { phase: "writing" } : {}) });
          },
          onEvent: ({ event }) => {
            if (event.type === EventType.RUN_ERROR) failed = true;
            if (event.type === EventType.TOOL_CALL_START) update({ phase: "querying" });
            if (event.type === EventType.TOOL_CALL_RESULT) update({ phase: "preparing" });
          },
          onRunFinishedEvent: ({ outcome }) => {
            finished = outcome === "success";
            interrupted = outcome === "interrupt";
          },
        },
      );
      if (!finished || failed || !answer.trim())
        throw new Error("Assistant run did not produce a complete answer");
      update({ status: "success" });
    } catch {
      update({ status: interrupted ? "interrupted" : "error" });
    } finally {
      clearTimeout(timeout);
      if (!finished || failed || controller.signal.aborted || !answer.trim()) {
        // Restore the local SDK state after an incomplete run. Server history is managed by the backend.
        agent.setMessages(beforeMessages);
        agent.setState(beforeState);
      }
      current.active = null;
    }
  }

  return {
    messages,
    validPeriod,
    pending: messages.some((message) => message.status === "pending"),
    ask: (question, request) => send(question, request),
    retry: (message) => send(message.question, message.request, message.id),
    newChat: () => {
      const previous = session.current;
      // Detach the old session first so late events cannot reach the new conversation.
      session.current = createSession();
      previous?.active?.controller.abort();
      previous?.agent?.abortRun();
      void previous?.agent?.detachActiveRun();
      setMessages([]);
    },
    stop: () => {
      const current = session.current;
      if (!current?.active) return;
      setMessages((previous) =>
        previous.map((message) =>
          message.status === "pending" ? { ...message, status: "stopped" } : message,
        ),
      );
      current.active.controller.abort();
      current.agent?.abortRun();
      void current.agent?.detachActiveRun();
    },
  };
}
